import React from 'react';
import { useDispatch } from 'react-redux';
import type { ClaimFile } from '../../../../api/files/interface';
import { type StoreState, useSelector } from '../../../../data/store';
import {
  actions as claimShellActions,
  ClaimOverviewShellState,
} from '../../data/logic';

interface UseFetchFilesHookState {
  files: {
    [fileId: string]: ClaimFile;
  };
  filePaths: {
    [fileId: string]: {
      [pageNumber: number]: {
        url: string;
        requestedDate: number;
      };
    };
  };
  actions: {
    fetchClaimFiles: ClaimOverviewShellState['actions']['fetchClaimFiles'];
    fetchClaimFile: ClaimOverviewShellState['actions']['fetchClaimFile'];
    fetchClaimFilePaths: ClaimOverviewShellState['actions']['fetchClaimFilePaths'];
  };
}

interface UseFetchFilesHookHandler {
  fetchFiles(): void;
  fetchFile(data: { fileId: string; stateDb?: boolean }): void;
  fetchFilePaths(data: {
    fileType: 'PDF_FROM_TEXT';
    dossierId: string;
    fileId: string;
  }): void;
  fetchPdfDocFilePaths(data: {
    dossierId: string;
    fileId: string;
    pages: [start: number, end: number];
  }): void;
}

const useFetchFilesHook = (
  dossierId: string
): [UseFetchFilesHookState, UseFetchFilesHookHandler] => {
  const dispatch = useDispatch();
  const { claimFiles, claimFilePaths, actions } = useSelector(
    (s: StoreState) => s['claim-overview-shell']
  );
  const handler = React.useMemo(
    () => ({
      fetchFiles: () => {
        dispatch(claimShellActions.fetchClaimFiles({ dossier_id: dossierId }));
      },
      fetchFile: (data: { fileId: string; stateDb?: boolean }) => {
        dispatch(
          claimShellActions.fetchClaimFile({
            dossier_id: dossierId,
            file_id: data.fileId,
            stateDb: data.stateDb,
          })
        );
      },
      fetchFilePaths: (data: {
        fileType: 'PDF_FROM_TEXT';
        dossierId: string;
        fileId: string;
      }) => {
        dispatch(
          claimShellActions.fetchClaimFilePaths({
            type: data.fileType,
            dossierId: data.dossierId,
            fileId: data.fileId,
          })
        );
      },
      fetchPdfDocFilePaths: (data: {
        dossierId: string;
        fileId: string;
        pages: [start: number, end: number];
      }) => {
        dispatch(
          claimShellActions.fetchClaimFilePaths({
            type: 'PDF',
            dossierId: data.dossierId,
            fileId: data.fileId,
            pages: data.pages,
          })
        );
      },
    }),
    [dossierId]
  );

  return [
    {
      files: claimFiles[dossierId] ?? {},
      filePaths: claimFilePaths,
      actions: {
        fetchClaimFiles: actions.fetchClaimFiles,
        fetchClaimFile: actions.fetchClaimFile,
        fetchClaimFilePaths: actions.fetchClaimFilePaths,
      },
    },
    handler,
  ];
};

export default useFetchFilesHook;
